<!--
 Copyright 2022 by domX Private Company

 All rights reserved,
 also regarding any disposal, exploitation, reproduction,
 editing, distribution, as well as in the event of
 applications for industrial property rights.

 This software contains proprietary and confidential
 information of domX Private Company.
 The disclosure of confidential information without
 express authorization is prohibited.

 This software may include Open Source Software.
 As far as Open Source Software is included,
 your rights for the respective Open Source Software
 components are governed by the applicable
 Open Source Software licenses.
-->

<template class="fill-height">    
  <v-card
      flat
      class="secondary fill-height pa-12"
      height="25%">
    <v-form
      v-if="deviceModel"
      ref="form"
      lazy-validation
    >
      <v-list class="secondary" min-width="900px" max-width="900px">
        <v-subheader class="display-1 grayBold--text">
          {{$t("edit_device")}}
        </v-subheader>
        <v-list-item>
          <v-list-item-content class="grayBold--text">
            <v-container>
              <v-row no-gutters>
                <v-col cols="6">
                  <v-text-field class="mr-5" disabled light v-model="deviceModel.device_id" append-icon="mdi-cellphone-cog" :label="$t('device_id')" />
                </v-col>
                <v-col cols="6">
                  <v-text-field disabled light v-model="deviceModel.serial_no" append-icon="mdi-cellphone-numeric" :label="$t('serial_number')" />
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="6" v-if="payload.device">
                  <v-autocomplete
                    light
                    class="grayBold--text mr-5"
                    v-model="deviceModel.technician_company_id"
                    prepend-inner-icon="mdi-domain"
                    :items="allAffiliations"
                    :item-text="affiliateCompanyText"
                    item-value="technician_group_id"
                    :label="$t('affiliated_companies')"
                    required
                    clearable
                    :loading="isLoadingAffiliations"
                    :disabled="isLoadingAffiliations"
                  >
                    <!-- <template v-slot:selection="data">
                      <v-chip
                        v-bind="data.attrs"
                        small
                        :input-value="data.selected"
                        class="black--text"
                        @click="data.select"
                        @click:close="remove(data.item)"
                        >
                        {{ `${data.item.assigned_entity_name}` }}
                      </v-chip>
                    </template> -->
                    <template v-slot:no-data>
                      <div class="px-4">{{ $t('comboNoData') }}</div>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="6">
                  <v-checkbox
                  v-show="!isNotAdmin"
                  light
                  class="pt-5 grayBold--text"
                  dense
                    v-model="deviceModel.editor_access"
                    :label="$t('editor_access_description')"
                ></v-checkbox>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="6">
                  <v-autocomplete
                    light
                    class="grayBold--text mr-5"
                    v-model="deviceModel.hvac_model_id"
                    :items="hvacItemModels"
                    item-text="brand_model"
                    item-value="model_id"
                    :label="$t('hvac_brand')"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
              <v-row no-gutters>
              <v-col cols="12">
                <v-textarea
                  light
                  v-model="deviceModel.notes"
                  :label="$t('notes')"
                  auto-grow
                  append-icon="mdi-text-box-edit-outline"
                  rows="1"
                  :counter="1000"
                  :rules="notesRule"/>
                </v-col>
              </v-row>
            </v-container>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <v-card-actions>
        <v-spacer/>
        <v-btn
          depressed
          right
          light
          color="grayBold--text secondary"
          @click="hideModal()"
        >
          {{$t("cancel")}} 
        </v-btn>
        <v-btn
          color="primary"
          depressed
          right
          large
          class="modal-update"
          @click="doUpdateDevice"><v-icon left>mdi-pencil</v-icon>{{$t("save")}}</v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator'
import { commonModule, distributorModule, itemsModule } from '@/store/modules/store-accessor'
import KeycloakSession from '@/helpers/keycloak-session'

@Component
export default class EditInventoryDeviceModal extends Vue {
  @Prop()
  payload!: any;
  loadingAffiliations = true;

  get currDevice () {
    return this.payload.device;
  }

  get allAffiliations() {
    return distributorModule.distributorAffiliations;
  }

  get isLoadingAffiliations () {
    return this.loadingAffiliations;
  }

  get hvacItemModels() {
    return itemsModule.hvacItemModels;
  }


  get kcSession() {
    return new KeycloakSession()
  }

  get isNotAdmin() {
      return this.kcSession.userIsNotAdmin();
  }

  deviceModel = {
    technician_company_id: null,
    device_id: '',
    serial_no: '',
    notes: null,
    editor_access: false,
    hvac_model_id: null,
  }

  notesRule= [
    (val: any) => {
      if(val != null) return val.length <= 1000 || this.$i18n.t("form_notes_length_rule");
      else return true;
    },
  ]

  //Add Validation for two 

  affiliateCompanyText(item: any) {
    return item.technician_name
  }

  async hideModal () {
    commonModule.hideModal();
  }

  async doUpdateDevice () {
    if ((this.$refs.form as Vue & { validate: () => boolean }).validate()) {
      await distributorModule.editDistributorDevice(this.deviceModel);
      distributorModule.getDevices('');
      this.hideModal();  
    }
  }

  async mounted() {
    commonModule.initSnackbar({});
    
    // Initialize deviceModel with payload data
    if (this.payload?.device) {
      this.deviceModel = {
        technician_company_id: this.payload.device.assigned_company_id ?? null,
        device_id: this.payload.device.device_id || '',
        serial_no: this.payload.device.serial_no || '',
        notes: this.payload.device.notes || null,
        editor_access: this.payload.device.editor_access || false,
        hvac_model_id: this.payload.device.hvac_model_id || null,
      };
    }
    
    this.loadingAffiliations = true;
    await distributorModule.getDistributorAffiliations();
    this.loadingAffiliations = false;
    itemsModule.getAllHVACItemModels('');
  }
};
</script>

